/**
 * TextStylesPanel.tsx
 * Panneau pour gérer les styles de texte prédéfinis
 */
import React, { useState, useCallback } from 'react';
import { useAppStore } from '../../services/StateService';
import type { TextStyle } from '../../types';
import { Modal } from './common/Modal';
import { ConfirmModal } from './common/ConfirmModal';

interface TextStylesPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TextStylesPanel: React.FC<TextStylesPanelProps> = ({ isOpen, onClose }) => {
  const savedStyles = useAppStore(state => state.textTool.savedStyles);
  const addTextStyle = useAppStore(state => state.addTextStyle);
  const updateTextStyle = useAppStore(state => state.updateTextStyle);
  const deleteTextStyle = useAppStore(state => state.deleteTextStyle);
  // P0.2 FIX: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
  const selectedNodeId = useAppStore(state => state.selection.primaryId);
  const shapes = useAppStore(state => state.shapes);
  const updateShape = useAppStore(state => state.updateShape);

  const [editingStyle, setEditingStyle] = useState<TextStyle | null>(null);
  const [newStyleName, setNewStyleName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  // A18 : style en attente de suppression (ouvre la ConfirmModal).
  const [deleteStyleId, setDeleteStyleId] = useState<string | null>(null);

  const selectedShape = selectedNodeId ? shapes.find(s => s.id === selectedNodeId) : null;
  const isTextShape = selectedShape?.type === 'text';

  // Créer un style depuis le texte sélectionné
  const handleCreateFromSelection = useCallback(() => {
    if (!selectedShape || selectedShape.type !== 'text') {
      return;
    }

    const newStyle: TextStyle = {
      id: `style-${Date.now()}`,
      name: newStyleName || 'Nouveau style',
      fontFamily: selectedShape.fontFamily || 'Arial',
      fontSize: selectedShape.fontSize || 16,
      fontWeight: selectedShape.fontStyle?.includes('bold') ? 700 : 400,
      color: selectedShape.fill || '#000000',
      lineHeight: 1.5,
      letterSpacing: 0,
    };

    addTextStyle(newStyle);
    setNewStyleName('');
    setShowCreateModal(false);
  }, [selectedShape, newStyleName, addTextStyle]);

  // Appliquer un style à un texte sélectionné
  const handleApplyStyle = useCallback(
    (style: TextStyle) => {
      if (!selectedShape || selectedShape.type !== 'text') {
        return;
      }

      updateShape(selectedShape.id, {
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fontStyle: style.fontWeight >= 600 ? 'bold' : 'normal',
        fill: style.color,
      });
    },
    [selectedShape, updateShape]
  );

  // Modifier un style
  const handleEditStyle = useCallback(
    (styleId: string, updates: Partial<TextStyle>) => {
      updateTextStyle(styleId, updates);
      setEditingStyle(null);
    },
    [updateTextStyle]
  );

  // A18 : ouvre la confirmation au lieu de confirm().
  const handleDeleteStyle = useCallback((styleId: string) => {
    setDeleteStyleId(styleId);
  }, []);

  const confirmDeleteStyle = useCallback(() => {
    if (deleteStyleId) deleteTextStyle(deleteStyleId);
    setDeleteStyleId(null);
  }, [deleteStyleId, deleteTextStyle]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Gestionnaire de styles de texte" size="large">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Instructions */}
          <div
            style={{
              padding: '12px',
              background: '#f3f4f6',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#374151',
            }}
          >
            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Comment utiliser les styles :</p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Créez des styles depuis un texte sélectionné</li>
              <li>Appliquez les styles à n'importe quel texte</li>
              <li>Modifiez ou supprimez les styles existants</li>
            </ul>
          </div>

          {/* Bouton créer depuis sélection */}
          {isTextShape && (
            <div>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                + Créer un style depuis le texte sélectionné
              </button>
            </div>
          )}

          {/* Liste des styles */}
          <div>
            <h3
              style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#111827' }}
            >
              Styles sauvegardés ({savedStyles.length})
            </h3>
            {savedStyles.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#6b7280',
                  background: '#f9fafb',
                  borderRadius: '6px',
                }}
              >
                Aucun style sauvegardé
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {savedStyles.map(style => (
                  <div
                    key={style.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: style.fontFamily,
                          fontSize: Math.min(style.fontSize, 24),
                          fontWeight: style.fontWeight,
                          color: style.color,
                          padding: '8px 12px',
                          background: '#f9fafb',
                          borderRadius: '4px',
                          minWidth: '80px',
                          textAlign: 'center',
                        }}
                      >
                        Aa
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                          {style.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {style.fontFamily} • {style.fontSize}px • {style.fontWeight}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {isTextShape && (
                        <button
                          onClick={() => handleApplyStyle(style)}
                          style={{
                            padding: '6px 12px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          Appliquer
                        </button>
                      )}
                      <button
                        onClick={() => setEditingStyle(style)}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDeleteStyle(style.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal créer style */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setNewStyleName('');
          }}
          title="Créer un style depuis le texte sélectionné"
          size="small"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label
                style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}
              >
                Nom du style
              </label>
              <input
                type="text"
                value={newStyleName}
                onChange={e => setNewStyleName(e.target.value)}
                placeholder="Ex: Titre principal"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
                autoFocus
              />
            </div>
            {selectedShape && selectedShape.type === 'text' && (
              <div
                style={{
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#6b7280',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Aperçu du style :</div>
                <div>
                  Police: {selectedShape.fontFamily || 'Arial'} • Taille:{' '}
                  {selectedShape.fontSize || 16}px
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewStyleName('');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateFromSelection}
                disabled={!newStyleName.trim()}
                style={{
                  padding: '8px 16px',
                  background: newStyleName.trim() ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newStyleName.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Créer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal modifier style */}
      {editingStyle && (
        <EditStyleModal
          style={editingStyle}
          onSave={updates => handleEditStyle(editingStyle.id, updates)}
          onClose={() => setEditingStyle(null)}
        />
      )}

      {/* A18 : confirmation de suppression de style */}
      <ConfirmModal
        isOpen={!!deleteStyleId}
        danger
        title="Supprimer ce style ?"
        message="Ce style de texte enregistré sera supprimé. Les textes existants ne sont pas modifiés."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={confirmDeleteStyle}
        onClose={() => setDeleteStyleId(null)}
      />
    </>
  );
};

/**
 * Modal pour modifier un style
 */
const EditStyleModal: React.FC<{
  style: TextStyle;
  onSave: (updates: Partial<TextStyle>) => void;
  onClose: () => void;
}> = ({ style, onSave, onClose }) => {
  const [name, setName] = useState(style.name);
  const [fontFamily, setFontFamily] = useState(style.fontFamily);
  const [fontSize, setFontSize] = useState(style.fontSize);
  const [fontWeight, setFontWeight] = useState(style.fontWeight);
  const [color, setColor] = useState(style.color);

  const handleSave = () => {
    onSave({
      name,
      fontFamily,
      fontSize,
      fontWeight,
      color,
    });
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Modifier le style" size="small">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label
            style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}
          >
            Nom
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>
        <div>
          <label
            style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}
          >
            Police
          </label>
          <select
            value={fontFamily}
            onChange={e => setFontFamily(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="Arial">Arial</option>
            <option value="Inter">Inter</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
          </select>
        </div>
        <div>
          <label
            style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}
          >
            Taille: {fontSize}px
          </label>
          <input
            type="range"
            min="8"
            max="200"
            value={fontSize}
            onChange={e => setFontSize(parseInt(e.target.value, 10))}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label
            style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}
          >
            Poids
          </label>
          <select
            value={fontWeight}
            onChange={e => setFontWeight(parseInt(e.target.value, 10))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value={400}>Normal</option>
            <option value={600}>Semi-bold</option>
            <option value={700}>Bold</option>
          </select>
        </div>
        <div>
          <label
            style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}
          >
            Couleur
          </label>
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
};
